<template>
  <q-table
    :data="mappedInvoices"
    :columns="columns"
    :pagination.sync="pagination"
    :rows-per-page-options="rowsPerPageOptions"
    row-key="id"
    table-class="invoice-table"
    hide-header
    hide-bottom
    binary-state-sort
  >
    <q-td slot="body-cell-invoice_number" slot-scope="props" :props="props">
      <router-link
        v-if="props.row.status.value !== 'draft'"
        :to="{ name: 'invoice',  params: { invoice: props.row.id, teamId: activeTeam.id } }"
        class="router-link"
      >
        {{ props.row.invoice_number }}
      </router-link>

      <router-link
        v-else
        :to="{ name: 'edit-invoice',  params: { invoice: props.row.id, teamId: activeTeam.id } }"
        class="router-link"
      >
        {{ $t('draft') }}
      </router-link>
    </q-td>

    <q-td slot="body-cell-created_at" slot-scope="props" :props="props">
      <span>
        {{ props.row.created_at_text }}

        <q-tooltip anchor="top middle" self="bottom middle" :delay="300" :offset="[0, 5]">
          {{ props.row.created_at_date }}
        </q-tooltip>
      </span>
    </q-td>

    <q-td slot="body-cell-status" slot-scope="props" :props="props">
      <status-chip
        :status="props.row.status.value"
        :committed="props.row.committed_at !== null"
        :color="props.row.status.color"
        :clickable="user.isUserOfActiveTeam()"
        @cancel="handleCancelInvoice(props.row.id)"
        @paid="handleMarkInvoiceAsPaid(props.row.id)"
        @open="handleMarkInvoiceAsOpen(props.row.id)"
        small
      />
    </q-td>

    <q-td slot="body-cell-actions" slot-scope="props" :props="props">
      <q-btn icon="more_vert" color="grey-7" flat round dense>
        <q-popover>
          <q-list link class="no-border">
            <q-item
              v-if="props.row.status.value !== 'draft'"
              v-close-overlay
              @click.native="$router.push({ name: 'invoice', params: { team: activeTeam.id, invoice: props.row.id } })"
            >
              <q-item-side icon="visibility"/>
              <q-item-main :label="$t('display')"/>
            </q-item>

            <q-item
              v-if="props.row.committed_at === null"
              v-close-overlay
              @click.native="$router.push({ name: 'edit-invoice', params: { team: activeTeam.id, invoice: props.row.id } })"
            >
              <q-item-side icon="edit"/>
              <q-item-main :label="$t('edit')"/>
            </q-item>

            <q-item
              v-if="props.row.status.value !== 'draft'"
              v-close-overlay
              @click.native="handlePdfDownload(props.row.id, props.row.invoice_number)"
            >
              <q-item-side icon="cloud_download"/>
              <q-item-main :label="$t('download_pdf')"/>
            </q-item>

            <template v-is-user>
              <q-item
                v-if="props.row.type !== 'imported_invoice'"
                v-close-overlay
                @click.native="handleCopy(props.row)"
              >
                <q-item-side icon="file_copy"/>
                <q-item-main :label="$t('duplicate')"/>
              </q-item>
            </template>

            <template v-if="props.row.committed_at === null && props.row.status.value !== 'draft'">
              <q-item-separator/>

              <q-item
                v-close-overlay
                v-is-user
                @click.native="handleCommit(props.row)"
              >
                <q-item-side icon="lock"/>
                <q-item-main :label="$t('commit')"/>
              </q-item>
            </template>
          </q-list>
        </q-popover>
      </q-btn>
    </q-td>
  </q-table>
</template>

<script>
  import { defaultColumns, defaultMapFunction } from './defaults'
  import { mapActions, mapGetters } from 'vuex'
  import FileSaver from 'file-saver'
  import { InvoiceClient } from 'src/api/Invoices'
  import StatusChip from 'components/Invoices/StatusChip.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import handleCancelInvoice from 'src/components/Invoices/Utils/cancel-invoice'
  import reopenInvoice from 'src/utils/reopenInvoice'
  import { tables } from 'src/config'

  export default {
    props: {
      invoices: {
        required: true,
        type: Array
      },

      columnsOverride: {
        required: false,
        type: Array,
        default: null
      },

      mapFunctionOverride: {
        required: false,
        type: Function,
        default: null
      }
    },

    data () {
      return {
        pagination: {
          page: 1,
          rowsPerPage: 0,
          descending: true,
          sortBy: 'invoice_number'
        },
        rowsPerPageOptions: tables.default_rows_per_page_options
      }
    },

    computed: {
      ...mapGetters(['activeTeam', 'user']),

      mapFunction () {
        return this.mapFunctionOverride ? this.mapFunctionOverride : defaultMapFunction
      },

      columns () {
        return this.columnsOverride ? this.columnsOverride : defaultColumns
      },

      mappedInvoices () {
        return this.mapFunction(this.invoices)
      }
    },

    methods: {
      ...mapActions(['addInvoicePayment', 'setInvoiceStatus', 'commitInvoice']),

      handleCancelInvoice,

      handleMarkInvoiceAsPaid (invoiceId) {
        this.addInvoicePayment({ invoiceId })
          .catch(createErrorFromResponse)
      },

      handleMarkInvoiceAsOpen (invoiceId) {
        reopenInvoice(invoiceId)
      },

      handlePdfDownload (invoiceId, name) {
        new InvoiceClient()
          .pdf(invoiceId)
          .then(response => {
            const blob = new Blob([response.data], { type: 'application/pdf' })
            FileSaver.saveAs(blob, this.$tc('invoice', 1) + '_' + name + '.pdf')
          })
      },

      handleCopy (invoice) {
        this.$router.push({
          name: 'create-invoice',
          params: { team: this.activeTeam.id },
          query: { copy: invoice.id }
        })
      },

      handleCommit (invoice) {
        this.$q.dialog({
          title: this.$t('confirm_commit_invoice_title'),
          message: this.$t('confirm_commit_invoice_body'),
          ok: this.$t('confirm'),
          cancel: this.$t('abort')
        })
          .then(() => {
            this.commitInvoice(invoice.id)
              .catch(createErrorFromResponse)
          })
          .catch(() => {})
      }
    },

    components: {
      StatusChip
    }
  }
</script>
